import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Keyboard, Platform, ScrollView, StyleSheet, View, Switch, Pressable, Animated } from 'react-native';
import { Button, Checkbox, Text, TextInput } from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { useAppColors } from '../../hooks/useAppColors';
import { useAuthStore } from '../../stores/authStore';
import { useSubmitAgreement } from '../../hooks/useLegal';
import { getApiError } from '../../utils/formatters';
import LegalDocumentViewer from './LegalDocumentViewer';
import Constants from 'expo-constants';
import * as Network from 'expo-network';

interface LegalDocumentSignerProps {
  docKey: string;
  title: string;
  url: string;
  affirmationKeys: string[];
  onSuccess: () => void;
  subUserName?: string;
  subUserId?: string;
  subUserAge?: number;
}

const GUARDIAN_WAIVER_KEY = 'guardian_waiver';
const CODE_OF_CONDUCT_KEYS = ['athlete_code_of_conduct', 'guardian_code_of_conduct'];
const DATA_CONSENT_KEY = 'legal.affirmations.guardian.data_consent';
const DATA_CONSENT_OVER_7_KEY = 'legal.affirmations.guardian.data_consent_over_7';

export default function LegalDocumentSigner({ docKey, title, url, affirmationKeys, onSuccess, subUserName, subUserId, subUserAge }: LegalDocumentSignerProps) {
  const { t, i18n } = useTranslation();
  const colors = useAppColors();
  const navigation = useNavigation<any>();
  const user = useAuthStore((s) => s.user);
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [isFormExpanded, setIsFormExpanded] = useState(false);
  const [affirmations, setAffirmations] = useState<Record<string, boolean>>({});
  const [signedName, setSignedName] = useState('');
  const [minorName, setMinorName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [formContentHeight, setFormContentHeight] = useState(0);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const submitMutation = useSubmitAgreement();
  const formScrollRef = useRef<ScrollView>(null);
  const formHeightAnim = useRef(new Animated.Value(0)).current;
  const formOpacityAnim = useRef(new Animated.Value(0)).current;
  const hasManuallyCollapsed = useRef(false);

  const isGuardianWaiver = docKey === GUARDIAN_WAIVER_KEY;
  const isCodeOfConduct = CODE_OF_CONDUCT_KEYS.includes(docKey);

  const handleReachedEnd = useCallback(() => setScrolledEnd(true), []);
  const handleViewerError = useCallback((msg: string) => setError(msg), []);

  useEffect(() => {
    setScrolledEnd(false);
    setIsFormExpanded(false);
    hasManuallyCollapsed.current = false;
    setAffirmations({});
    setSignedName('');
    setMinorName('');
    setError(null);
    setKeyboardHeight(0);
    setSubmitted(false);
    // Reset animations
    formHeightAnim.setValue(0);
    formOpacityAnim.setValue(0);
  }, [docKey, subUserId, formHeightAnim, formOpacityAnim]);

  // Track keyboard height so the overlay form sits above the keyboard
  useEffect(() => {
    const showEvent = Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent = Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const showSub = Keyboard.addListener(showEvent, (e) => setKeyboardHeight(e.endCoordinates.height));
    const hideSub = Keyboard.addListener(hideEvent, () => setKeyboardHeight(0));
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, []);

  const accountName = useMemo(() => {
    if (!user) return '';
    return `${user.first_name} ${user.last_name}`;
  }, [user]);

  const allAffirmed = useMemo(() => {
    return affirmationKeys.every((k) => affirmations[k]);
  }, [affirmationKeys, affirmations]);

  const guardianNameValid = useMemo(() => {
    if (isCodeOfConduct) return true;
    return signedName.trim().length > 0;
  }, [signedName, isCodeOfConduct]);

  const minorNameValid = useMemo(() => {
    if (!isGuardianWaiver || isCodeOfConduct) return true;
    const normalized = minorName.toLowerCase().replace(/\s/g, '');
    return normalized.length >= 3; // Require at least 3 chars for a valid name
  }, [minorName, isGuardianWaiver, isCodeOfConduct]);

  const canSubmit = scrolledEnd && allAffirmed && guardianNameValid && minorNameValid;

  // Auto-expand form once when user finishes scrolling PDF
  useEffect(() => {
    if (scrolledEnd && !isFormExpanded && !hasManuallyCollapsed.current) {
      setIsFormExpanded(true);
    }
  }, [scrolledEnd, isFormExpanded]);

  // Animate form expansion/collapse
  useEffect(() => {
    Animated.parallel([
      Animated.timing(formHeightAnim, {
        toValue: isFormExpanded ? 1 : 0,
        duration: 300,
        useNativeDriver: false,
      }),
      Animated.timing(formOpacityAnim, {
        toValue: isFormExpanded ? 1 : 0,
        duration: 250,
        useNativeDriver: false,
      }),
    ]).start();
  }, [isFormExpanded, formHeightAnim, formOpacityAnim]);

  const toggleAffirmation = (key: string) => {
    setAffirmations((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleFormExpansion = () => {
    setIsFormExpanded((prev) => {
      const next = !prev;
      if (!next) {
        hasManuallyCollapsed.current = true;
      }
      return next;
    });
  };

  // Resolve which i18n key to use for an affirmation (age-based variant for data_consent)
  const resolveAffirmationKey = (key: string): string => {
    if (key === DATA_CONSENT_KEY && subUserAge != null && subUserAge >= 7) {
      return DATA_CONSENT_OVER_7_KEY;
    }
    return key;
  };

  // Get affirmation label as plain text (for storage in affirmation_labels)
  const getAffirmationLabel = (key: string) => {
    const resolvedKey = resolveAffirmationKey(key);
    const label = t(resolvedKey, { childName: subUserName || '' });
    return label.replace('[[PP_LINK]]', t('auth.privacyPolicy'));
  };

  // Render affirmation text with [[PP_LINK]] replaced by a clickable Privacy Policy hyperlink
  const renderAffirmationText = (key: string) => {
    const resolvedKey = resolveAffirmationKey(key);
    const label = t(resolvedKey, { childName: subUserName || '' });
    const ppMarker = '[[PP_LINK]]';
    const idx = label.indexOf(ppMarker);

    if (idx === -1) {
      return (
        <Text style={[styles.affirmationText, { color: colors.text, opacity: scrolledEnd ? 1 : 0.5 }]}>
          {label}
        </Text>
      );
    }

    const before = label.substring(0, idx);
    const after = label.substring(idx + ppMarker.length);

    return (
      <Text style={[styles.affirmationText, { color: colors.text, opacity: scrolledEnd ? 1 : 0.5 }]}>
        {before}
        <Text
          style={styles.ppLink}
          onPress={() => navigation.navigate('PublicLegalDoc', { docType: 'privacy_policy' })}
        >
          {t('auth.privacyPolicy')}
        </Text>
        {after}
      </Text>
    );
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      const deviceInfo = await Network.getNetworkStateAsync().catch(() => null);
      const affirmationLabels = Object.fromEntries(affirmationKeys.map((k) => [k, getAffirmationLabel(k)]));

      // Generate User Agent string - use browser UA on web, expo-constants on mobile
      const userAgent = (() => {
        if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.userAgent) {
          return navigator.userAgent;
        }
        const appName = Constants.expoConfig?.name || 'CBJJ';
        const appVersion = Constants.expoConfig?.version || (Constants.manifest as any)?.version || '0.0.0';
        const systemName = Constants.platform?.ios ? 'iOS' : Constants.platform?.android ? 'Android' : 'Unknown';
        const systemVersion = Constants.systemVersion || 'Unknown';
        const deviceId = Constants.deviceId || Constants.expoGo?.deviceId || 'Unknown';
        const model = Constants.deviceName || Constants.platform?.ios?.model || 'Unknown';

        return `${appName}/${appVersion} (${systemName} ${systemVersion}; ${model}; ${deviceId})`;
      })();

      await submitMutation.mutateAsync({
        doc_key: docKey,
        language: i18n.language || 'en',
        signed_legal_name: isCodeOfConduct ? null : signedName.trim(),
        minor_legal_name: isGuardianWaiver && !isCodeOfConduct ? minorName.trim() : null,
        sub_user_id: subUserId ?? null,
        scrolled_completed: true,
        affirmations,
        doc_title: title,
        affirmation_labels: affirmationLabels,
        app_version: Constants.expoConfig?.version || (Constants.manifest as any)?.version || null,
        device_info: deviceInfo ? JSON.stringify({ type: deviceInfo.type, isConnected: deviceInfo.isConnected }) : null,
        user_agent: userAgent,
      });
      // Collapse the form immediately so it doesn't stay open for the next sub-user
      setSubmitted(true);
      setIsFormExpanded(false);
      hasManuallyCollapsed.current = true;
      onSuccess();
    } catch (err: any) {
      setError(getApiError(err, t('legal.signFailed'), t));
    }
  };

  return (
    <View style={styles.container}>
      <Text style={[styles.title, { color: colors.text }]}>{title}</Text>
      <View style={styles.viewer}>
        <LegalDocumentViewer key={`${docKey}-${subUserId ?? 'self'}`} url={url} onReachedEnd={handleReachedEnd} onError={handleViewerError} />
      </View>

      {/* Bottom overlay form — slides up over the viewer */}
      <View style={[styles.formOverlay, { bottom: keyboardHeight }]} pointerEvents="box-none">
        <Pressable
          onPress={toggleFormExpansion}
          style={[styles.formHeader, { backgroundColor: colors.bgHeader, borderColor: colors.border }]}
          accessibilityRole="button"
          accessibilityLabel={isFormExpanded ? t('legal.collapseForm') : t('legal.expandForm')}
          disabled={submitted}
        >
          <View style={styles.formHeaderContent}>
            <Text style={[styles.formHeaderText, { color: colors.text }]}>
              {submitted ? t('legal.signedTitle') : scrolledEnd ? t('legal.signingFormTitle') : t('legal.scrollToEndToEnable')}
            </Text>
            <Animated.View style={{ transform: [{ rotate: formHeightAnim.interpolate({
              inputRange: [0, 1],
              outputRange: ['0deg', '180deg']
            }) }] }}>
              <Ionicons
                name="chevron-up"
                size={24}
                color={colors.textSecondary}
              />
            </Animated.View>
          </View>
          {!scrolledEnd && (
            <View style={[styles.progressIndicator, { backgroundColor: colors.border }]}>
              <View style={[styles.progressBar, { backgroundColor: colors.accent, width: '0%' }]} />
            </View>
          )}
        </Pressable>

        {/* Collapsible Form Content */}
        <Animated.View style={[styles.formContainer, {
          maxHeight: formHeightAnim.interpolate({
            inputRange: [0, 1],
            outputRange: [0, Math.max(formContentHeight, 200)],
          }),
          opacity: formOpacityAnim,
          overflow: 'hidden',
          backgroundColor: colors.bg,
        }]} pointerEvents="auto">
          <ScrollView
            ref={formScrollRef}
            style={styles.form}
            contentContainerStyle={styles.formContent}
            keyboardShouldPersistTaps="handled"
            onContentSizeChange={(_, h) => setFormContentHeight(h)}
          >
        {!scrolledEnd && (
          <Text style={[styles.scrollHint, { color: colors.textSecondary }]}>
            {t('legal.scrollToEndToEnable')}
          </Text>
        )}
        {affirmationKeys.map((key) => (
          <View key={key} style={styles.row}>
            {Platform.OS === 'ios' ? (
              <Switch
                value={affirmations[key] || false}
                onValueChange={() => toggleAffirmation(key)}
                trackColor={{ false: colors.border, true: colors.accent }}
                thumbColor={affirmations[key] ? colors.accentText : colors.textSecondary}
                disabled={!scrolledEnd || submitted}
                style={{ marginRight: 8 }}
              />
            ) : (
              <Checkbox
                status={affirmations[key] ? 'checked' : 'unchecked'}
                onPress={() => toggleAffirmation(key)}
                color={colors.accent}
                disabled={!scrolledEnd || submitted}
              />
            )}
            {renderAffirmationText(key)}
          </View>
        ))}

        {/* Guardian Waiver: Minor Child Name Input (not required for code of conduct) */}
        {isGuardianWaiver && !isCodeOfConduct && (
          <>
            <TextInput
              label={t('legal.minorNameLabel')}
              value={minorName}
              onChangeText={setMinorName}
              onFocus={() => setTimeout(() => formScrollRef.current?.scrollToEnd({ animated: true }), 150)}
              style={[styles.input, { backgroundColor: colors.bgHeader }]}
              textColor={colors.text}
              outlineColor={colors.border}
              activeOutlineColor={colors.accent}
              mode="outlined"
              autoCorrect={false}
              placeholder={subUserName}
              disabled={!scrolledEnd || submitted}
            />
            {!minorNameValid && minorName.length > 0 && (
              <Text style={[styles.errorText, { color: colors.error }]}>{t('legal.minorNameRequired')}</Text>
            )}
          </>
        )}

        {/* Legal name input (not required for code of conduct) */}
        {!isCodeOfConduct && (
          <>
            <TextInput
              label={isGuardianWaiver ? t('legal.guardianNameLabel') : t('legal.signerNameLabel')}
              value={signedName}
              onChangeText={setSignedName}
              onFocus={() => setTimeout(() => formScrollRef.current?.scrollToEnd({ animated: true }), 150)}
              style={[styles.input, { backgroundColor: colors.bgHeader }]}
              textColor={colors.text}
              outlineColor={colors.border}
              activeOutlineColor={colors.accent}
              mode="outlined"
              autoCorrect={false}
              placeholder={accountName}
              disabled={!scrolledEnd || submitted}
            />
          </>
        )}
        {error && (
          <Text style={[styles.errorText, { color: colors.error }]}>{error}</Text>
        )}
        <Button
          mode="contained"
          onPress={handleSubmit}
          disabled={!canSubmit || submitMutation.isPending || submitted}
          loading={submitMutation.isPending || submitted}
          style={styles.button}
          buttonColor={colors.accent}
          textColor="#fff"
        >
          {submitted ? t('legal.loadingNextDoc') : t('legal.agreeAndSign')}
        </Button>
          </ScrollView>
        </Animated.View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  title: { fontSize: 18, fontWeight: '700', paddingHorizontal: 16, paddingTop: 12, paddingBottom: 4 },
  viewer: { flex: 1, minHeight: 200 },
  formOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  formHeader: {
    borderTopWidth: 1,
    borderColor: '#ccc',
    paddingHorizontal: 16,
    paddingVertical: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  formHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  formHeaderText: {
    fontSize: 15,
    fontWeight: '600',
    flex: 1,
  },
  progressIndicator: {
    height: 3,
    borderRadius: 2,
    marginTop: 8,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
  },
  formContainer: {
    // Height is animated
  },
  form: {
    // No maxHeight — sized by parent Animated.View
  },
  formContent: { padding: 16, paddingBottom: 32 },
  row: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  affirmationText: { flex: 1, fontSize: 14, marginLeft: 8 },
  ppLink: { textDecorationLine: 'underline', fontWeight: '500', color: '#3b82f6' },
  scrollHint: { fontSize: 13, marginBottom: 12, fontStyle: 'italic' },
  input: { marginTop: 8 },
  errorText: { fontSize: 13, marginTop: 4 },
  button: { marginTop: 16, borderRadius: 8 },
});
